import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { inject, makeStateKey, TransferState } from '@angular/core';
import { ApiKey, CustomRequest, LoggerPartialBody, RequestInterface, RequestOptions } from '@api/abstracts/models';
import { CACHE, CacheInput, CacheSettingsService, IS_CACHE_ENABLED, RETRY_WHEN_VISIBLE } from '@api/interceptors';
import { CustomError } from '@api/interceptors/models';
import { environment } from '@environments/environment';
import { ApiEndpoints, IntegrationConfig, Integrations } from '@environments/model';
import { CurrentLanguageService } from '@shared/services/language/current-language.service';
import { LANGUAGES } from '@shared/services/language/registry';
import { LogsService } from '@shared/services/logs';
import { LogLevel } from '@shared/services/logs/enums';
import { PlatformContextService } from '@shared/services/platform-context';
import { envConfiguredNetworkId, normalizeUrlPath } from '@shared/utils';
import { defer, EMPTY, MonoTypeOperatorFunction, Observable, of, take, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

const PII_FIELDS = [
  'email',
  'phone',
  'firstname',
  'lastname',
  'fullname',
  'message',
  'note',
  'project',
  'zipcode',
  'city',
  'address',
];

const isLanguageUrlPrefix = (segment: string): boolean =>
  Object.values(LANGUAGES).some((language) => language.urlPrefix === segment);

export abstract class ApiServiceBase<T> {
  protected readonly logs = inject(LogsService);
  protected readonly language = inject(CurrentLanguageService).language;
  protected readonly cacheSettings = inject(CacheSettingsService);
  private readonly http: HttpClient = inject(HttpClient);
  private readonly platformContext = inject(PlatformContextService);
  private readonly transferState = inject(TransferState);
  private readonly url!: string;
  private readonly apiKey?: ApiKey;

  protected constructor(
    protected endpoint: string,
    protected api: keyof ApiEndpoints = 'default',
    protected integrationApiKey?: keyof Integrations
  ) {
    this.url = this.url = environment.API.endpoint[api];
    this.apiKey = integrationApiKey
      ? this.parseApiKey((environment.integrations?.[integrationApiKey] as IntegrationConfig)?.apiKey ?? '')
      : undefined;
  }

  /**
   * Fetches a resource from the specified endpoint, optionally applying query parameters and caching options.
   *
   * @param id - The unique identifier of the resource to fetch. If not provided, a list of resources may be fetched.
   * @param queryOptions - A record of key-value pairs representing query parameters to include in the request URL. Optional.
   * @param cache - An optional caching configuration object to control caching behavior.
   * @returns An `Observable<T>` that emits the requested resource of type `T` when the HTTP request is successful.
   *
   * @example
   * ```typescript
   * // Fetch a specific resource by ID
   * service.read('123').subscribe((data) => {
   *   console.log(data);
   * });
   *
   * // Fetch a list of resources with query parameters
   * service.read('', { search: 'example', limit: '10' }).subscribe((data) => {
   *   console.log(data);
   * });
   *
   * // Fetch with caching options
   * service.read('123', {}, { cacheExpirationTime: 600 }).subscribe((data) => {
   *   console.log(data);
   * });
   * ```
   */
  read(id: string, queryOptions: Record<string, string> = {}, cache?: CacheInput): Observable<T> {
    const options = this.buildRequestOptions(cache);
    const queryParams = this.objectToQuerystring(queryOptions);
    let url = '';
    if (id && id.length > 0) {
      url = normalizeUrlPath(`${this.url}/${this.endpoint}/${id}${queryParams}`);
    } else {
      url = normalizeUrlPath(`${this.url}/${this.endpoint}${queryParams}`);
    }
    return this.http.get<T>(url, options).pipe(
      tap((response) => {
        this.logger({ method: 'GET', url }, response);
      })
    );
  }

  /**
   * Sends a POST request to the configured Elasticsearch endpoint and retrieves a response of the specified type.
   *
   * This method constructs a URL based on the endpoint and sends the provided `payload` using the POST method.
   * It supports optional caching, which can be passed via the `cache` parameter.
   * Logs the request and response details after execution.
   *
   * @typeParam T - The type of the response expected from the Elasticsearch server.
   * @param queryId - A unique identifier for this query, used for debugging purposes in browser network tools.
   * @param payload - The payload to be sent in the POST request body. This can be any data structure
   * compatible with Elasticsearch's expected payload format.
   * @param cache - (Optional) Defines cache-related options for the request. If provided, it influences
   * whether the request reuses cached data or fetches new data. The `cache` parameter is of type `CacheInput`.
   * @return An observable that emits the response from the Elasticsearch server cast to the specified type `T`.
   * The observable emits the server response and completes.
   */
  readElastic<T>(queryId: string, payload: unknown, cache?: CacheInput): Observable<T> {
    const options = this.buildRequestOptions(cache);
    options.context?.set(RETRY_WHEN_VISIBLE, true);
    const url = `${this.url}/${this.endpoint}&q=${queryId}`;
    return this.http.post<T>(url, payload, options).pipe(
      tap((response) => {
        this.logger({ method: 'POST', url }, response, payload as LoggerPartialBody);
      })
    );
  }

  /**
   * Fetches a slug-based page from the CMS.
   *
   * Where {@link PlatformContextService.readsDrupal} says so, Drupal answers `/slug?slug=X` with any
   * `queryOptions` (notably `preview=true` when the contributor wants to see drafts). Elsewhere the
   * pre-generated JSON `/prefetch/X.json` is read from {@link PlatformContextService.prefetchBaseUrl}.
   * The homepage of a prefixed language (`X` = `es`) is read from its folder,
   * `/prefetch/es/homepage.json`, where the generation stores it. In the browser, the first read of a slug answers
   * with the page the server rendered, without a request.
   *
   * @param slugOverride - A specific slug to override the default endpoint. If `null`, the default endpoint is used.
   * @param queryOptions - Query parameters appended only when hitting Drupal (e.g. `preview=true`).
   *                       Ignored when reading the static JSON (which does not accept query params).
   * @param cache - An optional caching configuration object.
   *
   * @return An `Observable` emitting the typed resource (matches the `ApiServiceBase<T>` generic).
   */
  readSlug(
    slugOverride: string | null = null,
    queryOptions: Record<string, string> = {},
    cache?: CacheInput
  ): Observable<T> {
    const options = { ...this.buildRequestOptions(cache), transferCache: false };
    if (slugOverride !== null) {
      this.endpoint = slugOverride;
    }
    const reuseServerResponse = this.reuseServerResponse<T>(`slug:${this.endpoint}`);

    if (this.platformContext.readsDrupal()) {
      const extraParams = this.objectToQuerystring(queryOptions).replace(/^\?/, '&');
      const url = `${this.url}/slug?slug=${this.endpoint}${extraParams}`;
      return this.http.get<T>(url, options).pipe(
        tap((response) => {
          this.logger({ method: 'GET', url }, response);
        }),
        reuseServerResponse
      );
    }

    const prefetchPath = isLanguageUrlPrefix(this.endpoint) ? `${this.endpoint}/homepage` : this.endpoint;
    const url = normalizeUrlPath(`${this.platformContext.prefetchBaseUrl()}/prefetch/${prefetchPath}.json`);
    return this.http.get<T>(url, { ...options, responseType: 'json' }).pipe(
      tap((response) => {
        this.logger({ method: 'GET', url }, response);
      }),
      catchError((err: unknown) => {
        // The ResponseInterceptor wraps every HttpErrorResponse into a CustomError ({status, error, server}),
        // so we read the HTTP status from there rather than from HttpErrorResponse directly.
        if ((err as CustomError)?.status === 404) {
          const fallbackUrl = this.getLocalizedFallbackUrl(this.endpoint);
          this.logs.warn('ApiServiceBase', `Prefetch JSON not found at ${url}, falling back to ${fallbackUrl}`);
          return this.http.get<T>(fallbackUrl, { ...options, responseType: 'json' }).pipe(
            tap((response) => {
              this.logger({ method: 'GET', url: fallbackUrl }, response);
            }),
            catchError((fallbackErr: unknown) => {
              this.logs.error('ApiServiceBase', 'Prefetch JSON and its localized 404 fallback are both missing', {
                url,
                fallbackUrl,
                error: fallbackErr,
              });
              return throwError(() => fallbackErr);
            })
          );
        }
        return throwError(() => err);
      }),
      reuseServerResponse
    );
  }

  /**
   * Fetches a non-slug Drupal resource (e.g. `common` global configuration) exposed at
   * its own dedicated endpoint (`${apiUrl}/${name}`), with the same dispatch and first read as `readSlug`. The static
   * read fetches `${name}.json` from the prefetch folder of the current language
   * (`/prefetch/es/common.json`, `/prefetch/common.json` for French).
   *
   * @param name - The resource name, used both as Drupal endpoint segment and JSON file name.
   * @param cache - Optional caching configuration.
   */
  readResource<R>(name: string, cache?: CacheInput): Observable<R> {
    const options = { ...this.buildRequestOptions(cache), transferCache: false };
    const reuseServerResponse = this.reuseServerResponse<R>(`resource:${name}`);

    if (this.platformContext.readsDrupal()) {
      const url = normalizeUrlPath(`${this.url}/${name}`);
      return this.http.get<R>(url, options).pipe(
        tap((response) => {
          this.logger({ method: 'GET', url }, response);
        }),
        reuseServerResponse
      );
    }

    const folder = this.language.urlPrefix ? `${this.language.urlPrefix}/` : '';
    const url = normalizeUrlPath(`${this.platformContext.prefetchBaseUrl()}/prefetch/${folder}${name}.json`);
    return this.http.get<R>(url, { ...options, responseType: 'json' }).pipe(
      tap((response) => {
        this.logger({ method: 'GET', url }, response);
      }),
      reuseServerResponse
    );
  }

  /**
   * Answers the browser's first read of `key` with the response the server rendered the page with, which the HTML
   * carries whichever address each side reads. See « Premier affichage » in `docs/architecture.md`.
   */
  private reuseServerResponse<R>(key: string): MonoTypeOperatorFunction<R> {
    const stateKey = makeStateKey<R>(`${this.language.langcode}:${key}`);
    if (this.platformContext.isServer()) {
      return tap((response) => this.transferState.set(stateKey, response));
    }
    return (read$) =>
      defer(() => {
        const response = this.transferState.get<R | undefined>(stateKey, undefined);
        this.transferState.remove(stateKey);
        return response === undefined ? read$ : of(response);
      });
  }

  /**
   * Builds the URL of the localized 404 prefetch JSON used as fallback when the
   * primary prefetch lookup returns a 404.
   *
   * The endpoint's first segment is inspected against the URL prefixes of {@link LANGUAGES}:
   * if it matches a known localized prefix (e.g. `es`, `ca`), the fallback URL preserves
   * that prefix (`/prefetch/es/404.json`). Otherwise the default French 404 is used
   * (`/prefetch/404.json`). The fallback is served as the response body so the browser
   * URL stays intact.
   */
  private getLocalizedFallbackUrl(endpoint: string): string {
    const firstSegment = endpoint.split('/', 1)[0] ?? '';
    const localePath = isLanguageUrlPrefix(firstSegment) ? `${firstSegment}/` : '';
    return normalizeUrlPath(`${this.platformContext.prefetchBaseUrl()}/prefetch/${localePath}404.json`);
  }

  /**
   * Creates a new resource of type `T` by sending a POST request to the API endpoint.
   *
   * @template T - The generic type of the resource being managed.
   *
   * @param item - The resource data to be created. It must exclude the `id` property,
   *               as `id` is typically generated by the backend.
   * @param includeFournierNetwork - A boolean indicating whether to include the
   *                                 Fournier Network information in the request body.
   *                                 Defaults to `false`.
   * @param cache - (Optional) Defines cache-related options for the request. If provided, it influences
   * whether the request reuses cached data or fetches new data. The `cache` parameter is of type `CacheInput`.
   * @returns An `Observable` that emits the created resource of type `T`.
   *          This observable allows you to subscribe to the HTTP response and
   *          handle the response data, errors, or completion events.
   */
  create(item: Omit<T, 'id'>, includeFournierNetwork = false, cache?: CacheInput): Observable<T> {
    const options = this.buildRequestOptions(cache);
    const url = `${this.url}/${this.endpoint}`;

    const fournierNetwork = envConfiguredNetworkId(this.language.region);
    const body = includeFournierNetwork && fournierNetwork ? { ...item, network: fournierNetwork } : item;

    return this.http.post<T>(url, body, options).pipe(
      tap((response) => {
        this.logger({ method: 'POST', url }, response, item);
      })
    );
  }

  /**
   * Updates a specific property of a resource identified by `id` using a partial update approach.
   * This method sends an HTTP PATCH request to update the resource while complying with
   * the `application/merge-patch+json` content type.
   *
   * @param id - The unique identifier of the resource to be updated.
   * @param item - A partial object of type `T`, containing the properties to be updated on the resource.
   *               Only the specified properties will be updated.
   * @param includeFournierNetwork - (Optional) Flag indicating whether to include Fournier Network query parameters.
   *                                 Defaults to `false`. If `true`, additional query parameters related
   *                                 to the Fournier Web Service network ID will be appended to the request.
   * @returns An `Observable` emitting the updated resource of type `T` when the HTTP PATCH request completes successfully.
   */
  updateProp(id: string, item: Partial<T>, includeFournierNetwork = false): Observable<T> {
    const headers = new HttpHeaders().set('Content-Type', 'application/merge-patch+json');
    const options = this.buildRequestOptions(undefined, headers);

    let queryOptions = {};
    if (includeFournierNetwork) {
      const fournierNetwork = envConfiguredNetworkId(this.language.region);
      queryOptions = { networkId: fournierNetwork };
    }
    const queryParams = this.objectToQuerystring(queryOptions);

    const url = `${this.url}/${this.endpoint}/${id}${queryParams}`;
    return this.http.patch<T>(url, item, options).pipe(
      take(1),
      tap((response) => {
        this.logger({ method: 'PATCH', url }, response, item);
      })
    );
  }

  /**
   * Sends a custom HTTP request based on the provided configuration.
   *
   * This method dynamically handles HTTP requests (e.g., GET, POST, PATCH, PUT, DELETE)
   * based on the parameters specified in the `CustomRequest` object. It uses reactive
   * programming patterns with RxJS, allowing easy subscription to the response.
   * Logging of the request and response is performed internally using a custom logger.
   *
   * @template Y - The expected type of the response data.
   *
   * @param {CustomRequest} request - The configuration object for the HTTP request.
   * Contains information such as the method, URL, body, options, cache, and query parameters.
   *
   * - `request.method` - The HTTP method (`GET`, `POST`, `PATCH`, `PUT`, or `DELETE`).
   * - `request.url` (optional) - The relative or external URL for the request.
   * - `request.externalUrl` (optional) - Fully qualified external URL, taking precedence over `url`.
   * - `request.body` (optional) - The body of the request, used for `POST`, `PATCH`, and `PUT` methods.
   * - `request.options` (optional) - Additional options for the HTTP request.
   * - `request.query` (optional) - Query parameters for the request, serialized into the URL for `GET` requests.
   * - `request.cache` (optional) - Caching information, used to generate request options.
   *
   * @return {Observable<Y>} An `Observable` that emits the response of the request. The response's type
   * is determined by the generic type `Y`. For one-time requests, the response is piped to emit a single
   * value. For `EMPTY` responses, no action is performed.
   *
   * @example
   * // Example usage of the `customRequest` method for a `POST` request
   * const request: CustomRequest = {
   *   method: 'POST',
   *   url: 'users',
   *   body: { name: 'John Doe', email: 'john.doe@example.com' },
   * };
   * this.customRequest<User>(request).subscribe((response) => {
   *   console.log(response);
   * });
   *
   * @example
   * // Example usage of the `customRequest` method for a `GET` request
   * const request: CustomRequest = {
   *   method: 'GET',
   *   url: 'users',
   *   query: { id: 42 },
   * };
   * this.customRequest<User>(request).subscribe((response) => {
   *   console.log(response);
   * });
   */
  customRequest<Y>(request: CustomRequest): Observable<Y> {
    const requestUrl = request.url ? `/${request.url}` : '';
    const url = request.externalUrl ?? `${this.url}/${this.endpoint}${requestUrl}`;
    let options: Record<string, unknown> = {
      ...(request.options ?? {}),
    };
    switch (request.method) {
      case 'POST':
        return this.http.post<Y>(url, request.body ?? {}).pipe(
          take(1),
          tap((response) => {
            this.logger({ method: 'POST', url }, response, request.body as LoggerPartialBody);
          })
        );
      case 'PATCH':
        return this.http.patch<Y>(url, request.body ?? {}).pipe(
          take(1),
          tap((response) => {
            this.logger({ method: 'PATCH', url }, response, request.body as LoggerPartialBody);
          })
        );
      case 'PUT':
        return this.http.put<Y>(url, request.body ?? {}).pipe(
          take(1),
          tap((response) => {
            this.logger({ method: 'PUT', url }, response, request.body as LoggerPartialBody);
          })
        );
      case 'GET': {
        options = {
          ...options,
          ...this.buildRequestOptions(request.cache, request.options?.headers),
        };
        const queryParams = this.objectToQuerystring(request.query ?? {});
        return this.http.get<Y>(`${url}${queryParams}`, options).pipe(
          tap((response) => {
            this.logger({ method: 'GET', url }, response);
          })
        );
      }
      case 'DELETE':
        return this.http.delete<Y>(url).pipe(
          take(1),
          tap((response) => {
            this.logger({ method: 'DELETE', url }, response);
          })
        );
      default:
        return EMPTY;
    }
  }

  /**
   * Retrieves the cache options for the application, creating and configuring an HTTP context
   * with cache-related settings if a cache input is provided.
   *
   * @param cache - An optional object of type `CacheInput` that contains cache configuration properties.
   *                The supported properties include:
   *                - `cacheExpirationTime` (number): The expiration time for the cache.
   *                - `forcedKey` (string): A key to force cache usage.
   *                - `refresh` (boolean): Flag indicating whether the cache should be refreshed.
   *                - `store` (any): The store to be used for caching.
   * @returns An object containing an optional `context` of type `HttpContext`.
   *          The `context` includes cache-related configurations if a `cache` input is provided,
   *          or it is empty if no cache settings are applied.
   */
  private getCacheOptions(cache?: CacheInput): {
    context?: HttpContext;
  } {
    const context = new HttpContext();
    if (cache) {
      context.set(IS_CACHE_ENABLED, this.cacheSettings.enabled());
      context.set(CACHE, {
        cacheExpirationTime: cache.cacheExpirationTime,
        forcedKey: cache.forcedKey,
        refresh: cache.refresh,
        store: cache.store,
      });
    }
    return { context };
  }

  /**
   * Builds and returns a set of request options, including headers and optional caching options.
   * This method ensures the inclusion of an Authorization header if an API key is configured,
   * and combines any provided headers with those already present in the options.
   *
   * @param cache - An optional `CacheInput` value specifying caching-related options for the request.
   * @param headers - An optional `HttpHeaders` instance containing any HTTP headers to append
   *                  or overwrite in the request.
   *
   * @return A `RequestOptions` object containing the merged cache settings and HTTP headers,
   *         which can be used in an HTTP request.
   */
  private buildRequestOptions(cache?: CacheInput, headers?: HttpHeaders): RequestOptions {
    let options: RequestOptions = this.getCacheOptions(cache);
    if (this.apiKey && this.apiKey.type === 'Bearer') {
      if (headers) {
        headers = headers.append('Authorization', `Bearer ${this.apiKey.value}`);
      } else {
        headers = new HttpHeaders().set('Authorization', `Bearer ${this.apiKey.value}`);
      }
    }
    if (headers) {
      let finalHeaders = (options.headers as HttpHeaders) || new HttpHeaders();

      headers.keys().forEach((key) => {
        const value = headers.get(key);
        if (value) {
          finalHeaders = finalHeaders.set(key, value);
        }
      });

      options = {
        ...options,
        headers: finalHeaders,
      };
    }
    return options;
  }

  /**
   * Parses a raw API key string into its structured components.
   *
   * The primary purpose of this method is to extract and separate the `type`
   * and `value` from a raw API key string formatted as `<type>value`.
   * If the string does not match the expected pattern, the method will treat
   * it as a generic API key. Extra validation ensures that neither the `type`
   * nor the `value` is empty.
   *
   * @param rawKey - The raw API key string that may optionally follow the pattern `<type>value`.
   *                 If the string does not conform to this pattern or contains invalid data,
   *                 it will be treated as a generic API key.
   *
   * @returns An object of type `ApiKey` containing `type` and `value` if the raw key is valid and
   *          matches the pattern. Returns `undefined` if the input string does not meet validation
   *          criteria or the extracted components are invalid.
   *
   * @example
   * ```typescript
   * // Example with a structured API key:
   * const apiKey = parseApiKey("<custom>abc123");
   * console.log(apiKey); // { type: "custom", value: "abc123" }
   *
   * // Example with a generic API key:
   * const genericKey = parseApiKey("abc123");
   * console.log(genericKey); // { type: "api_key", value: "abc123" }
   *
   * // Example with invalid input:
   * const invalidKey = parseApiKey("< >");
   * console.log(invalidKey); // undefined
   * ```
   */
  private parseApiKey(rawKey: string): ApiKey | undefined {
    const regex = /^<(?<type>[^>]+)>(?<value>.+)$/;
    const match = regex.exec(rawKey);
    if (!match?.groups) return { type: 'api_key', value: rawKey };

    const { type, value } = match.groups;

    // Ensure neither part is empty (extra validation)
    if (!type?.trim() || !value?.trim()) return undefined;
    return { type, value };
  }

  /**
   * Logs the details of an API request, including the HTTP method, URL, request body (if applicable),
   * and the response. Designed for debugging purposes and uses the `LogsService` for structured logging.
   *
   * @param req - The API request object implementing the `RequestInterface`. Contains details such as
   *              the HTTP method and URL of the request.
   * @param response - The response object from the API call. Can be of any type and will be logged
   *                   for debugging.
   * @param body - (Optional) A partial body of the request, adhering to the `LoggerPartialBody` type. It
   *               defaults to an empty object. If provided and the request method is `POST`, `PUT`, or
   *               `PATCH`, it will include the body in the logs.
   *
   * @return void - This method does not return a value. It performs logging operations.
   */
  private logger(req: RequestInterface, response: unknown, body: LoggerPartialBody = {}): void {
    this.logs.groupStart(
      LogLevel.DEBUG,
      true,
      'ApiServiceBase',
      `${req.method.toUpperCase()} ${this.stripSensitiveParams(req.url)}`
    );
    if (['POST', 'PUT', 'PATCH'].includes(req.method)) {
      const bodyItem = body?.body ?? body;
      this.logs.debug('Body', this.maskPiiFields(bodyItem));
    }
    this.logs.debug('Response', response);
    this.logs.groupEnd(LogLevel.DEBUG);
  }

  /**
   * Replaces known PII fields (email, phone, names, address, free-text fields) with `'[redacted]'`
   * in a request body before it is logged, recursing into nested objects and arrays.
   *
   * @param value - The request body (or nested value) to mask.
   * @returns A copy of the value with PII fields redacted, or the value itself if it is not an object.
   */
  private maskPiiFields(value: unknown): unknown {
    // Local dev (applicationType LOCAL) logs real request bodies; every other env redacts PII.
    if (environment.applicationType === 'LOCAL') {
      return value;
    }
    if (Array.isArray(value)) {
      return value.map((item) => this.maskPiiFields(item));
    }
    if (!value || typeof value !== 'object') {
      return value;
    }
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, val]) =>
        PII_FIELDS.includes(key.toLowerCase()) ? [key, '[redacted]'] : [key, this.maskPiiFields(val)]
      )
    );
  }

  /**
   * Removes sensitive query parameters (`token` and the configured API key parameter) from a URL
   * so credentials never appear in logged request labels.
   *
   * @param url - The request URL, possibly carrying a query string.
   * @returns The URL without its sensitive query parameters.
   */
  private stripSensitiveParams(url: string): string {
    const queryIndex = url.indexOf('?');
    if (queryIndex === -1) {
      return url;
    }
    const sensitiveParams = ['token', this.apiKey?.type.toLowerCase()];
    const params = url
      .slice(queryIndex + 1)
      .split('&')
      .filter((param) => !sensitiveParams.includes(param.split('=')[0]?.toLowerCase() ?? ''));
    const baseUrl = url.slice(0, queryIndex);
    return params.length > 0 ? `${baseUrl}?${params.join('&')}` : baseUrl;
  }

  /**
   * Converts an object into a URL query string representation.
   *
   * This method takes an object and generates a query string by encoding
   * its keys and values. If the object includes nested objects, their values
   * are stringified before encoding. Additionally, if an API key is set in
   * the context, it will automatically append it to the query string.
   *
   * @param obj - An object where keys are strings, and values are strings or numbers.
   *              It represents the parameters to be encoded into the query string.
   *              For example: `{ key1: 'value1', key2: 123 }`.
   *
   * @return A URL query string starting with a `?` character, or an empty string
   *         if the provided object is empty or undefined.
   *         For example, calling with `{ key1: 'value1', key2: 123 }` would return
   *         `"?key1=value1&key2=123"`.
   */
  private objectToQuerystring(obj: Record<string, string | number>): string {
    if (this.apiKey && this.apiKey.type !== 'Bearer' && this.apiKey.value) {
      obj = {
        ...obj,
        ...{ [this.apiKey.type]: this.apiKey.value },
      };
    }
    if (!obj) {
      return '';
    }
    return Object.keys(obj).reduce((str, key, i) => {
      const delimiter = i === 0 ? '?' : '&';
      key = encodeURIComponent(key);
      let val;
      if (typeof obj[key] === 'object') {
        val = encodeURIComponent(JSON.stringify(obj[key]));
      } else {
        val = encodeURIComponent(obj[key]!);
      }
      return val === 'undefined' ? str : [str, delimiter, key, '=', val].join('');
    }, '');
  }
}
